import { useFetch } from 'shared/hooks/use-fetch'
import { RequestMethodsEnum } from 'shared/hooks/use-fetch/types'
import useUser from 'shared/hooks/use-user'
import { LimitType, SwrPaginationInterface } from 'shared/types/pagination-interface'
import { getPaginationAndFilterQueryString } from 'shared/utils/api/get-pagination-and-filter-query-string'
import { NOTIFICATIONS_API_URL } from '../api/notifications-api'
import {
  NotificationsApiInterface,
  NotificationsFilterInterface,
} from '../types/notification-types'
import { useControlledSwr } from 'shared/hooks/use-controlled-swr'
import { RequestModuleEnum } from 'shared/enums/request-module-enum'
import { useWaitedSharingIdsStorage } from './use-waited-sharing-ids-storage'
import { NotificationTypeEnum } from 'modules/profile/notification-settings/enums/notification-settings-enum'

type UseNotificationsInterface = Omit<SwrPaginationInterface, 'limit'> & {
  shouldFetchNotifications?: boolean
  filter: NotificationsFilterInterface
  limit: LimitType | number
}

export interface HasUnreadResponse {
  hasUnread: boolean
}

export const useNotifications = ({
  limit,
  shouldFetchNotifications = true,
  pagination,
  filter,
}: UseNotificationsInterface) => {
  const { user } = useUser()
  const { fetcher, isFetching } = useFetch<RequestMethodsEnum.get, NotificationsApiInterface>({
    method: RequestMethodsEnum.get,
  })

  const { fetcher: unreadFetcher } = useFetch<RequestMethodsEnum.get, HasUnreadResponse>({
    method: RequestMethodsEnum.get,
  })

  const { getItem, setItem, removeItem } = useWaitedSharingIdsStorage()

  const { data: notifications, mutate } = useControlledSwr<NotificationsApiInterface>(
    () => {
      if (user && shouldFetchNotifications) {
        return [
          `${NOTIFICATIONS_API_URL}/notifications?${getPaginationAndFilterQueryString({
            limit: limit as LimitType,
            pagination,
            filter: { ...filter },
          })}`,
          RequestModuleEnum.Notifications,
        ]
      }
      return null
    },
    fetcher,
    {
      refreshInterval(latestData) {
        const waitedSharingIds = getItem()

        if (!waitedSharingIds?.size) return 0

        const gottenSharingIds = latestData?.items
          ?.filter(notification => {
            return notification.eventType === NotificationTypeEnum.SharingStarted
          })
          .map(notification => notification.data.sharingId)

        const justGottenSharingIds = waitedSharingIds.intersection(new Set(gottenSharingIds))

        const notGottenSharingIds = waitedSharingIds.difference(justGottenSharingIds)

        if (notGottenSharingIds.size) {
          setItem(notGottenSharingIds)
          return 1500
        } else {
          removeItem()
          return 0
        }
      },
      errorRetryCount: 3,
    },
  )

  const { data: notificationsMeta, mutate: mutateNotificationsMeta } =
    useControlledSwr<HasUnreadResponse>(() => {
      if (user) {
        return [
          `${NOTIFICATIONS_API_URL}/notifications/has-unread`,
          RequestModuleEnum.Notifications,
        ]
      }
      return null
    }, unreadFetcher)

  return {
    notificationsMeta,
    mutateNotificationsMeta,
    notifications,
    isFetching,
    mutate,
  }
}
